import { useCallback, useEffect, useMemo, useState } from 'react';
import InstanceApi from '../api/instance/InstanceApi';
import HostApi from '../api/host/HostApi';
import AgentApi from '../api/instance/AgentApi';
import TagApi from '../api/TagApi';
import InstanceTarget from '../types/instance/InstanceTarget';
import InstanceTargetHost from '../types/instance/InstanceTargetHost';
import HostTarget from '../types/host/HostTarget';
import Agent from '../types/instance/Agent';

interface FilterState {
    sensorstatus: string[];
    type: string[];
    host: string[];
    tag: string[];
    hostType: string[];
    agent: string[];
}

interface FilterOptions {
    sensorstatuses: Array<{ label: string; value: string }>;
    types: string[];
    hosts: string[];
    tags: Array<{ label: string; value: string }>;
    agents: Array<{ label: string; value: string }>;
}

interface UseInstanceFiltersOptions {
    /**
     * If provided, uses these instances instead of fetching
     */
    instances?: InstanceTarget[];
    /**
     * If true, will fetch instances. Hosts and mappings are always fetched unless skipHostFetch is true.
     */
    shouldFetch?: boolean;
    /**
     * If true, skips fetching hosts and mappings (use when they're provided externally)
     */
    skipHostFetch?: boolean;
    /**
     * Initial filter state
     */
    initialFilters?: Partial<FilterState>;
}

interface UseInstanceFiltersReturn {
    // Filter state
    filters: FilterState;
    updateFilter: (filterName: string, values: string[]) => void;
    clearAllFilters: () => void;
    
    // Data
    instances: InstanceTarget[];
    allHosts: HostTarget[];
    datasourceIdToHostIdMap: Record<number, number[]>;
    datasourceHostTypeMap: Record<number, string[]>;
    
    // Filter options
    availableFilterOptions: FilterOptions;
    availableHostTypes: string[];
    
    // Helpers
    getSensorStatusLabel: (instance: InstanceTarget) => string;
    getFilteredInstances: (instancesToFilter?: InstanceTarget[]) => InstanceTarget[];
    
    // Loading state
    filtersLoading: boolean;
}

const defaultFilters: FilterState = {
    sensorstatus: [],
    type: [],
    host: [],
    tag: [],
    hostType: [],
    agent: []
};

/**
 * Custom hook for managing instance filtering logic
 * Handles fetching instances, hosts, mappings, and computing filter options
 */
export function useInstanceFilters(options: UseInstanceFiltersOptions = {}): UseInstanceFiltersReturn {
    const { 
        instances: providedInstances, 
        shouldFetch = false,
        skipHostFetch = false,
        initialFilters = {}
    } = options;

    const [filters, setFilters] = useState<FilterState>({
        ...defaultFilters,
        ...initialFilters
    });
    
    const [instances, setInstances] = useState<InstanceTarget[]>(providedInstances || []);
    const [allHosts, setAllHosts] = useState<HostTarget[]>([]);
    const [datasourceIdToHostIdMap, setDatasourceIdToHostIdMap] = useState<Record<number, number[]>>({});
    const [datasourceHostTypeMap, setDatasourceHostTypeMap] = useState<Record<number, string[]>>({});
    const [filtersLoading, setFiltersLoading] = useState<boolean>(true);
    const [agents, setAgents] = useState<Agent[]>([]);

    // Fetch agents on mount
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const fetchedAgents = await AgentApi.getAgents();
                setAgents(fetchedAgents);
            } catch (error) {
                console.error('Failed to fetch agents for filtering:', error);
            }
        };
        fetchAgents();
    }, []);

    // Update instances when providedInstances changes and fetch tags if needed
    useEffect(() => {
        const updateInstances = async () => {
            if (!providedInstances || providedInstances.length === 0) return;
            
            // Check if instances already have tags
            const hasTagsAlready = providedInstances.some(inst => inst.tags !== undefined);
            
            if (hasTagsAlready) {
                // Already have tags, just use them - only update if instances actually changed to avoid infinite loop
                setInstances(prev => {
                    if (prev.length === providedInstances.length &&
                        prev.every((p, i) => p.id === providedInstances[i]?.id)) {
                        return prev;
                    }
                    return providedInstances;
                });
            } else {
                // Fetch all tags at once and map them to instances
                try {
                    const allTags = await TagApi.getAllRawTags();
                    
                    // Group tags by datasourceid
                    const tagsByInstanceId = allTags.reduce((acc, tag) => {
                        if (tag.datasourceid !== undefined) {
                            if (!acc[tag.datasourceid]) {
                                acc[tag.datasourceid] = {};
                            }
                            acc[tag.datasourceid][tag.tagname] = tag.tagvalue;
                        }
                        return acc;
                    }, {} as Record<number, Record<string, string>>);

                    // Map tags to instances
                    const instancesWithTags = providedInstances.map(instance => ({
                        ...instance,
                        tags: tagsByInstanceId[instance.id] || {}
                    }));
                    
                    setInstances(instancesWithTags);
                } catch (error) {
                    console.error("Failed to fetch tags for instances:", error);
                    setInstances(providedInstances);
                }
            }
        };
        
        updateInstances();
    }, [providedInstances]);

    // Fetch instances if shouldFetch is true
    useEffect(() => {
        const fetchInstances = async () => {
            try {
                setFiltersLoading(true);
                
                // Fetch instances with tags in bulk
                const instancesWithTags = await InstanceApi.getInstancesWithTags();

                setInstances(instancesWithTags);
                setFiltersLoading(false);
            } catch (error) {
                console.error("Failed to fetch instances for filtering:", error);
                setFiltersLoading(false);
            }
        };

        if (shouldFetch) {
            fetchInstances();
        } else {
            setFiltersLoading(false);
        }
    }, [shouldFetch]);

    // Fetch hosts and mappings (unless skipHostFetch is true)
    useEffect(() => {
        const fetchHostsAndMappings = async () => {
            try {
                const [
                    fetchedHosts,
                    datasourceHostMapResponse
                ] = await Promise.all([
                    HostApi.getHosts(),
                    InstanceApi.getInstanceHostMapping()
                ]);

                // Build host type map
                const hostTypeMapById = fetchedHosts.reduce((acc, host: HostTarget) => {
                    acc[host.id] = host.type;
                    return acc;
                }, {} as Record<number, string>);

                // Build datasource to host type map
                const dsHostTypeMap = datasourceHostMapResponse.reduce((acc, mapping: InstanceTargetHost) => {
                    const hostType = hostTypeMapById[mapping.hostid];
                    if (hostType) {
                        if (!acc[mapping.id]) {
                            acc[mapping.id] = [];
                        }
                        if (!acc[mapping.id].includes(hostType)) {
                            acc[mapping.id].push(hostType);
                        }
                    }
                    return acc;
                }, {} as Record<number, string[]>);

                // Build datasource to host ID map
                const dsToHostIdMap = datasourceHostMapResponse.reduce((acc, mapping: InstanceTargetHost) => {
                    if (!acc[mapping.id]) {
                        acc[mapping.id] = [];
                    }
                    acc[mapping.id].push(mapping.hostid);
                    return acc;
                }, {} as Record<number, number[]>);

                setAllHosts(fetchedHosts);
                setDatasourceHostTypeMap(dsHostTypeMap);
                setDatasourceIdToHostIdMap(dsToHostIdMap);
            } catch (error) {
                console.error("Failed to fetch hosts and mappings for filtering:", error);
            }
        };

        if (!skipHostFetch) {
            fetchHostsAndMappings();
        }
    }, [skipHostFetch]);

    // Helper for Sensor Status (memoized to prevent infinite loops)
    const getSensorStatusLabel = useCallback((instance: InstanceTarget): string => {
        if (instance.state?.toLowerCase() !== 'started') return 'Offline';
        if (instance.sensorstatus === 200) return 'Healthy';
        return 'Issue';
    }, []);

    // Helper to update filters (memoized to prevent infinite loops)
    const updateFilter = useCallback((filterName: string, values: string[]) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: values
        }));
    }, []);

    // Clear all filters (memoized to prevent infinite loops)
    const clearAllFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);

    // Calculate unique filter options from instances
    const availableFilterOptions = useMemo((): FilterOptions => {
        const sensorstatuses = new Set<string>();
        const types = new Set<string>();
        const hosts = new Set<string>();
        const tagValues = new Set<string>();
        const agentIds = new Set<number>();
        
        instances.forEach(instance => {
            sensorstatuses.add(getSensorStatusLabel(instance));
            if (instance.type) types.add(instance.type);
            if (instance.agentid) agentIds.add(instance.agentid);
            const hostIds = datasourceIdToHostIdMap[instance.id];
            if (hostIds && Array.isArray(hostIds)) {
                hostIds.forEach(hostId => {
                    const mappedHost = allHosts.find(h => h.id === hostId);
                    if (mappedHost && mappedHost.name) {
                        hosts.add(mappedHost.name);
                    }
                });
            }
            // Collect all tag key=value pairs
            if (instance.tags) {
                Object.entries(instance.tags).forEach(([key, value]) => {
                    tagValues.add(`${key}=${value}`);
                });
            }
        });

        // Map agent IDs to names
        const agentOptions = Array.from(agentIds)
            .map(agentId => {
                const agent = agents.find(a => a.id === agentId);
                return {
                    label: agent?.name || `Agent ${agentId}`,
                    value: agentId.toString()
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        return {
            sensorstatuses: Array.from(sensorstatuses).sort().map(status => ({ label: status, value: status })),
            types: Array.from(types).sort(),
            hosts: Array.from(hosts).sort(),
            tags: Array.from(tagValues).sort().map(tag => ({ label: tag, value: tag })),
            agents: agentOptions
        };
    }, [instances, allHosts, datasourceIdToHostIdMap, agents, getSensorStatusLabel]);

    // Compute available host types from allHosts
    const availableHostTypes = useMemo(() => {
        const types = allHosts.map(h => h.type).filter(Boolean);
        return Array.from(new Set(types));
    }, [allHosts]);

    // Helper function to filter instances based on current filter state
    const getFilteredInstances = useCallback((instancesToFilter?: InstanceTarget[]): InstanceTarget[] => {
        const targetInstances = instancesToFilter || instances;
        let filtered = targetInstances;

        // Filter by Sensor Status
        if (filters.sensorstatus && filters.sensorstatus.length > 0) {
            filtered = filtered.filter(instance =>
                filters.sensorstatus.includes(getSensorStatusLabel(instance))
            );
        }

        // Filter by Type
        if (filters.type.length > 0) {
            filtered = filtered.filter(instance =>
                instance.type && filters.type.includes(instance.type)
            );
        }

        // Filter by Host Type
        if (filters.hostType.length > 0) {
            filtered = filtered.filter(instance => {
                const hostTypes = datasourceHostTypeMap[instance.id];
                if (!hostTypes || hostTypes.length === 0) return false;
                return hostTypes.some(hostType =>
                    filters.hostType.some(
                        filterType => filterType.toLowerCase().trim() === hostType.toLowerCase().trim()
                    )
                );
            });
        }

        // Filter by Host
        if (filters.host.length > 0) {
            filtered = filtered.filter(instance => {
                const hostIds = datasourceIdToHostIdMap[instance.id];
                if (!hostIds || hostIds.length === 0) return false;
                return hostIds.some(hostId => {
                    const mappedHost = allHosts.find(h => h.id === hostId);
                    return mappedHost && filters.host.includes(mappedHost.name);
                });
            });
        }

        // Filter by Tags
        if (filters.tag.length > 0) {
            filtered = filtered.filter(instance => {
                if (!instance.tags) return false;
                return filters.tag.every(tag => {
                    const [key, value] = tag.split('=');
                    return instance.tags && instance.tags[key] === value;
                });
            });
        }

        // Filter by Agent
        if (filters.agent.length > 0) {
            filtered = filtered.filter(instance =>
                instance.agentid && filters.agent.includes(instance.agentid.toString())
            );
        }

        return filtered;
    }, [filters, instances, allHosts, datasourceIdToHostIdMap, datasourceHostTypeMap, getSensorStatusLabel]);

    return {
        filters,
        updateFilter,
        clearAllFilters,
        instances,
        allHosts,
        datasourceIdToHostIdMap,
        datasourceHostTypeMap,
        availableFilterOptions,
        availableHostTypes,
        getSensorStatusLabel,
        getFilteredInstances,
        filtersLoading
    };
}

